import { useEffect, useRef } from 'react';

interface SwipeOpts {
  onLeft?: () => void;
  onRight?: () => void;
  threshold?: number;
}

/** Attaches horizontal swipe handlers to an element. */
export const useSwipe = <T extends HTMLElement>(opts: SwipeOpts) => {
  const ref = useRef<T | null>(null);
  // Keep latest opts in a ref so the effect doesn't re-bind listeners every render
  // when the caller passes a fresh literal (e.g. `{ onLeft, onRight }`).
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let startX = 0;
    let startY = 0;
    let active = false;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      startX = e.touches[0]!.clientX;
      startY = e.touches[0]!.clientY;
      active = true;
    };
    const onEnd = (e: TouchEvent) => {
      if (!active) return;
      active = false;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      const threshold = optsRef.current.threshold ?? 60;
      if (Math.abs(dx) < threshold) return;
      if (Math.abs(dy) > Math.abs(dx) * 0.7) return; // ignore mostly-vertical
      if (dx > 0) optsRef.current.onRight?.();
      else optsRef.current.onLeft?.();
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend', onEnd);
    };
  }, []);

  return ref;
};

interface LongPressOpts {
  onLongPress: () => void;
  delay?: number;
}

/** Long-press handlers (returns props to spread on element). */
export const useLongPress = ({ onLongPress, delay = 450 }: LongPressOpts) => {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggered = useRef(false);

  const cancel = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  // Make sure we don't fire after unmount.
  useEffect(() => () => cancel(), []);

  return {
    onTouchStart: (e: React.TouchEvent) => {
      triggered.current = false;
      cancel();
      timer.current = setTimeout(() => {
        triggered.current = true;
        onLongPress();
      }, delay);
    },
    onTouchEnd: () => cancel(),
    onTouchMove: () => cancel(),
    onContextMenu: (e: React.MouseEvent) => {
      // Right-click on desktop also triggers long-press menu
      e.preventDefault();
      onLongPress();
    },
    onClickCapture: (e: React.MouseEvent) => {
      if (triggered.current) {
        e.preventDefault();
        e.stopPropagation();
        triggered.current = false;
      }
    },
  };
};
